import type { IAppContext } from 'App/AppContext';
import { IdentityProviderType } from 'models/identityProvider';
import { PreviewFeatureToggles, Settings, SkillControlEnabled } from 'models/setting';
import { FeatureTypes, hasUserFeature } from 'services/Auth/featurePermissions';
import { CopilotPanelContext } from './copilotBridge';

export type PreviewFeatureToggleValues = Record<string, boolean>;

export type PreviewFeatureToggleDefinition = {
  code: string;
  description: string;
  // Shown in the tenant admin list instead of the code. The code is a persisted setting key that the
  // server reads too, so it cannot be renamed to tidy up the UI; set a label to rename just the row.
  label?: string;
  context?: CopilotPanelContext;
};

export const ProcessesPreviewFeatureToggleCode = 'copilot.processes';
export const SemanticSearchPreviewFeatureToggleCode = 'search.semantic';
export const AgentToolPreviewFeatureToggleCode = 'copilot.agentTool.preview';
export const ControlSceneAIActionsPreviewFeatureToggleCode = 'copilot.controlSceneAIActions';

// Gates the Projects module, until it is GA. The server reads the same code
// (`ProjectTierResolver.ProjectPreviewFeatureCode`) and refuses Project writes without it.
export const ProjectsPreviewFeatureToggleCode = 'projects.preview';

// Gates the thumbs under an AI answer: the insight card's feedback row, a Copilot panel answer and a
// room summary. UI-only for now: the verdict is not sent anywhere yet, so the server has nothing to read.
export const AIFeedbackPreviewFeatureToggleCode = 'copilot.feedback';

// Gates library categories on a document provider other than SharePoint (Google Drive, Nextcloud,
// Notion, Loop): the admin tab, the provider choice when creating a category, and the pickers. The
// server reads the same code (DocumentProviderController.PreviewFeatureCode) and serves only
// SharePoint and virtual categories without it.
export const DocumentProvidersPreviewFeatureToggleCode = 'library.documentproviders';

// REDLAB's half of the SkillControl switch (phases 20R and 22): whether the organization sees the integration at all,
// which shows the SkillControl tab in Settings > Integration. The organization's half is the setting SkillControlEnabled
// the admin switches there; every SkillControl surface asks isSkillControlEnabled, which requires both. The server
// reads the same pair (SkillControlOptions.PreviewFeatureCode plus Setting.SkillControlEnabled, ISkillControlSwitch).
export const SkillControlPreviewFeatureToggleCode = 'skillControl';

export const copilotPreviewFeatureToggleDefinitions: PreviewFeatureToggleDefinition[] = [
  {
    code: ProcessesPreviewFeatureToggleCode,
    description: 'Enable Copilot process suggestions in the Processes scene.',
    context: CopilotPanelContext.Processes,
  },
  {
    code: SemanticSearchPreviewFeatureToggleCode,
    description: 'Enable semantic global search.',
    context: CopilotPanelContext.Generic,
  },
  {
    code: AgentToolPreviewFeatureToggleCode,
    label: 'copilot.agentTool',
    description: 'Enable preview agent tools.',
    context: CopilotPanelContext.Generic,
  },
  {
    code: ProjectsPreviewFeatureToggleCode,
    label: 'module.projects',
    description: 'Enable the Projects module.',
  },
  {
    code: ControlSceneAIActionsPreviewFeatureToggleCode,
    label: 'copilot.audit',
    description: 'Enable copilot audit action and audit criteria.',
    context: CopilotPanelContext.Generic,
  },
  {
    code: AIFeedbackPreviewFeatureToggleCode,
    description: 'Enable thumbs up and down under AI answers.',
  },
  {
    code: DocumentProvidersPreviewFeatureToggleCode,
    label: 'library.documentProviders',
    description: 'Enable library categories on Google Drive, Nextcloud, Notion and Microsoft Loop.',
  },
  {
    code: SkillControlPreviewFeatureToggleCode,
    label: 'integration.skillControl',
    description: 'Enable the SkillControl integration.',
  },
];

export const normalizePreviewFeatureToggles = (value: unknown): PreviewFeatureToggleValues => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

  return value as PreviewFeatureToggleValues;
};

export const getPreviewFeatureToggles = (settings: Settings | undefined): PreviewFeatureToggleValues => {
  try {
    return normalizePreviewFeatureToggles(settings?.get(PreviewFeatureToggles));
  } catch {
    return {};
  }
};

export const getPreviewFeatureToggleStates = (values: PreviewFeatureToggleValues): PreviewFeatureToggleValues => {
  return copilotPreviewFeatureToggleDefinitions.reduce<PreviewFeatureToggleValues>((current, definition) => {
    current[definition.code] = values[definition.code] === true;

    return current;
  }, {});
};

export const isPreviewFeatureToggleEnabled = (settings: Settings | undefined, code: string): boolean => {
  const toggles = getPreviewFeatureToggles(settings);

  return toggles[code] === true;
};

// PLAN-075 (generic audit criteria, assessments, the criteria columns and the AI outcomes) is a preview: every web surface
// of it asks this, the Audit feature and the copilot.audit toggle both. The API is not gated.
export const isAuditCriteriaPreviewEnabled = (appContext: IAppContext): boolean =>
  hasUserFeature(appContext, FeatureTypes.Audit) &&
  isPreviewFeatureToggleEnabled(appContext.globalDataCache.settings, ControlSceneAIActionsPreviewFeatureToggleCode);

export const isProjectsModulePreviewEnabled = (settings: Settings | undefined): boolean => {
  return isPreviewFeatureToggleEnabled(settings, ProjectsPreviewFeatureToggleCode);
};

// For an Entra tenant: without this toggle the library behaves exactly as it did before providers
// existed: SharePoint categories and the virtual ones from content packages, and no way to add
// anything else. A Google or OIDC organization has nothing but its providers, so for it the
// toggle does not apply.
export const isDocumentProvidersEnabled = (
  settings: Settings | undefined,
  tenantIdentityProviderType: IdentityProviderType,
): boolean => {
  return (
    isPreviewFeatureToggleEnabled(settings, DocumentProvidersPreviewFeatureToggleCode) ||
    tenantIdentityProviderType !== IdentityProviderType.Entra
  );
};

// The SkillControl switch: REDLAB's preview toggle and the organization's own setting, both on. A cache from before the
// setting existed reads as off (Settings.get answers undefined for a name it does not hold).
export const isSkillControlEnabled = (settings: Settings | undefined): boolean => {
  return (
    isPreviewFeatureToggleEnabled(settings, SkillControlPreviewFeatureToggleCode) &&
    settings?.get(SkillControlEnabled) === true
  );
};

export const isCopilotPanelContextEnabled = (settings: Settings | undefined, context: CopilotPanelContext): boolean => {
  const definition = copilotPreviewFeatureToggleDefinitions.find((toggle) => toggle.context === context);
  if (!definition) return false;

  return isPreviewFeatureToggleEnabled(settings, definition.code);
};
